import { useState } from "react";
import type { ForteXApi, LayerData, LayerState } from "@fortex/api";
import { ForteXApiError } from "@fortex/api";
import { Button, Chip, Field, Input, Skeleton, TextArea, errorMessage, format, intlFor } from "@fortex/map";
import type { ChipTone } from "@fortex/map";
import { useApp } from "../../app/context";
import { useLoad } from "../../app/useLoad";
import type { AppLabels } from "../../i18n";

export type LayerStateLabels = Pick<AppLabels, "layerStateNotCalculated" | "layerStateCalculating" | "layerStateCalculated" | "layerStateFailed">;

export const layerStateLabelKeys: Record<LayerState, keyof LayerStateLabels> = {
  NotCalculated: "layerStateNotCalculated",
  Calculating: "layerStateCalculating",
  Calculated: "layerStateCalculated",
  Failed: "layerStateFailed"
};

export const layerStateTones: Record<LayerState, ChipTone> = { NotCalculated: "neutral", Calculating: "accent", Calculated: "success", Failed: "danger" };

export function LayerStateChip({ state, labels }: { state: LayerState; labels: LayerStateLabels }) {
  return (
    <Chip tone={layerStateTones[state]} data-fxmap="layer-state" data-state={state}>
      {labels[layerStateLabelKeys[state]]}
    </Chip>
  );
}

export type LayerDataFieldsProps = {
  api: ForteXApi;
  id: number;
  labels: AppLabels;
  readOnly: boolean;
  version: number;
};

export function LayerDataFields({ api, id, labels, readOnly, version }: LayerDataFieldsProps) {
  const { toast, lang } = useApp();
  const data = useLoad<LayerData>(signal => api.layerData(id, signal), [id, version]);
  const [refreshing, setRefreshing] = useState(false);
  const intl = intlFor(lang);
  const layer = data.data;

  const refresh = async () => {
    setRefreshing(true);

    try {
      const result = await api.refreshLayerData(id);

      data.setData(() => result);
    } catch (reason) {
      if (reason instanceof ForteXApiError && reason.status === 409) data.setData(previous => (previous === null ? previous : { ...previous, state: "Calculating" }));
      else toast("danger", errorMessage(reason, labels));
    } finally {
      setRefreshing(false);
    }
  };

  const coordinate = (value: number | undefined) => (value === undefined ? "" : intl.number(value, 7));

  return (
    <section className="fxapp-settings-section" data-fxmap="layer-data">
      <h3 className="fxapp-settings-title">{labels.layerData}</h3>
      {layer === null ? (
        data.error !== null ? (
          <p className="fxapp-form-error" role="alert">
            {errorMessage(data.error, labels)}
          </p>
        ) : (
          <Skeleton lines={3} />
        )
      ) : (
        <>
          <div className="fxapp-toolbar">
            <LayerStateChip state={layer.state} labels={labels} />
            <span className="fxapp-toolbar-grow" />
            {readOnly ? null : <Button kind="ghost" size="sm" icon="redo" label={labels.refresh} busy={refreshing} disabled={layer.state === "Calculating"} onClick={() => void refresh()} data-fxmap="layer-data-refresh" />}
          </div>
          <div className="fxapp-settings" data-fxmap="layer-data-bounds">
            <h4 className="fxapp-settings-title">{labels.layerDataBounds}</h4>
            <div className="fxapp-form-row">
              <Field label={labels.layerDataMinLat}>
                <Input mono readOnly value={coordinate(layer.bounds?.minLat)} />
              </Field>
              <Field label={labels.layerDataMinLon}>
                <Input mono readOnly value={coordinate(layer.bounds?.minLon)} />
              </Field>
              <Field label={labels.layerDataMaxLat}>
                <Input mono readOnly value={coordinate(layer.bounds?.maxLat)} />
              </Field>
              <Field label={labels.layerDataMaxLon}>
                <Input mono readOnly value={coordinate(layer.bounds?.maxLon)} />
              </Field>
            </div>
          </div>
          <div className="fxapp-form-row">
            <Field label={labels.layerDataTable}>
              <Input mono readOnly value={layer.tableName ?? ""} data-fxmap="layer-data-table" />
            </Field>
            <Field label={labels.folder}>
              <Input mono readOnly value={layer.folder ?? ""} data-fxmap="layer-data-folder" />
            </Field>
            <Field label={labels.sldLayer}>
              <Input mono readOnly value={layer.layer ?? ""} data-fxmap="layer-data-sld" />
            </Field>
          </div>
          <div className="fxapp-form-row">
            <Field label={labels.layerDataCalculatedDate}>
              <Input readOnly value={intl.dateTime(layer.calculatedDate)} data-fxmap="layer-data-date" />
            </Field>
            <Field label={labels.layerDataDuration}>
              <Input readOnly value={layer.durationMs === null ? "" : format(labels.layerDataSeconds, { seconds: intl.number(layer.durationMs / 1000, 1) })} data-fxmap="layer-data-duration" />
            </Field>
          </div>
          <Field label={labels.layerDataError}>
            <TextArea mono rows={3} readOnly value={layer.error ?? ""} data-fxmap="layer-data-error" />
          </Field>
        </>
      )}
    </section>
  );
}
